import { useState, useEffect } from 'react';
import { 
  Button, 
  Grid, 
  Box, 
  Typography, 
  CircularProgress,  
  useMediaQuery, 
  useTheme,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  TextField,
  InputAdornment,
  Toolbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import {
  People as EmployeesIcon,
  ShoppingBasket as ProductsIcon,
  Code as CodesIcon,
  Group as ClientsIcon,
  Mail as MailIcon,
  Receipt as ChecksIcon,
  BarChart as AnalyticsIcon,
  LocationOn as AddressesIcon
} from '@mui/icons-material';
import { fetchData, fetchProducts, deleteProduct, fetchClients } from '../api/github';
import EmployeeCard from '../components/EmployeeCard';
import Sidebar from '../components/Sidebar';
import AddEmployeeModal from '../components/AddEmployeeModal';
import ProductsList from '../components/ProductsList';
import AddProductModal from '../components/AddProductModal';
import ClientList from '../components/ClientList';
import AddClientModal from '../components/AddClientModal';
import CodesManager from '../components/CodesManager';
import ChecksList from '../components/ChecksList';
import MailPhotoList from '../components/MailPhotoList';
import MailList from '../components/MailList';
import FloatingActionButton from '../components/FloatingActionButton';
import LoadingAnimation from '../components/LoadingAnimation';
import ReviewsIcon from '@mui/icons-material/Reviews';
import ReviewsList from '../components/ReviewsList';
import TemplatesList from '../components/TemplatesList';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import Analytics from '../components/Analytics';
import AddressesList from '../components/AddressesList';

const MainContent = styled(Box)(({ theme }) => ({
  marginLeft: 250,
  width: `calc(100% - 250px)`,
  padding: theme.spacing(3),
  minHeight: '100vh',
}));

const sections = [
  { id: 'employees', label: 'Ходимлар', icon: <EmployeesIcon /> },
  { id: 'products', label: 'Махсулотлар', icon: <ProductsIcon /> },
  { id: 'clients', label: 'Мижозлар', icon: <ClientsIcon /> },
  { id: 'codes', label: 'Кодлар', icon: <CodesIcon /> },
  { id: 'mail', label: 'Почта', icon: <MailIcon /> },
  { id: 'checks', label: 'Чеклар', icon: <ChecksIcon /> },
  { id: 'mailPhoto', label: 'Почта Расм', icon: <ChecksIcon /> },
  { id: 'reviews', label: 'Отзывы', icon: <ReviewsIcon /> },
  { id: 'templates', label: 'Шаблоны', icon: <FormatAlignLeftIcon /> },
  { id: 'analytics', label: 'Аналитика', icon: <AnalyticsIcon /> },
  { id: 'addresses', label: 'Манзиллар', icon: <AddressesIcon /> }
];

export default function AdminDashboard() {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [products, setProducts] = useState([]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const employeeId = localStorage.getItem('userId');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productFilterField, setProductFilterField] = useState('all');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Управление историей для кнопки "Назад"
  useEffect(() => {
    // При смене activeSection добавляем запись в историю
    if (activeSection) {
      window.history.pushState({ section: activeSection }, '', `#${activeSection}`);
    } else {
      // Для главного меню заменяем текущую запись
      window.history.replaceState({ section: null }, '', '#home');
    }

    // Обработчик события popstate для аппаратной кнопки "Назад"
    const handlePopState = (event) => {
      event.preventDefault();
      if (activeSection) {
        // Если в разделе, возвращаемся в главное меню
        setActiveSection(null);
        // Заменяем текущую запись в истории, чтобы избежать дублирования
        window.history.replaceState({ section: null }, '', '#home');
      } else {
        // Если в главном меню, можно разрешить выход (или ничего не делать)
        // Если хочешь запретить выход, убери window.history.back()
        // window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Очистка обработчика при размонтировании
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [activeSection]);

  const handleDeleteProduct = async (id) => {
    try {
      await deleteProduct(id);
      await loadProducts();
    } catch (error) {
      console.error("Ошибка при удалении:", error);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    if (shouldRefresh) {
      setRefreshTrigger(prev => prev + 1);
    }
    setClientModalOpen(false);
    setProductModalOpen(false);
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await fetchProducts();
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    return productFilterField === 'name' && product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
           productFilterField === 'all' && product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
           productFilterField === 'price' && product.price.toString().includes(productSearchTerm) ||
           productFilterField === 'stock' && product.stock.toString().includes(productSearchTerm);
  });

  const loadClients = async () => {
    try {
      const data = await fetchClients();
      let filtered = data;
      
      if (employeeFilter !== 'all') {
        filtered = data.filter(client => 
          client.orders.some(order => order.employeeId === employeeFilter)
        );
      }
      
      setClients(filtered);
    } catch (error) {
      console.error("Ошибка загрузки клиентов:", error);
    }
  };

  useEffect(() => {
    if (activeSection === 'products') loadProducts();
    if (activeSection === 'clients') loadClients();
  }, [activeSection, refreshTrigger]);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const data = await fetchData('employees.json');

      const filtered = data.filter(e => e.role !== 'admin');
      const sorted = filtered.sort((a, b) => 
        new Date(b.lastLogin || 0) - new Date(a.lastLogin || 0)
      );
      setEmployees(sorted);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const handleBackToList = () => {
    setActiveSection(null);
    // Обновляем историю, чтобы отражать главное меню
    window.history.replaceState({ section: null }, '', '#home');
  };

  return (
    <Box display="flex" sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
      <Sidebar showMenuButton={!activeSection} />
      
      <MainContent sx={{
        marginLeft: 0,
        mt: 0,
        width: isMobile ? '100%' : 'calc(100% - 250px)',
        padding: isMobile ? 0 : 1
      }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 0 : 2 }}>
          {activeSection ? (
            <Box>
              <AppBar 
                position="fixed" 
                color="transparent" 
                elevation={0}
                sx={{ mb: 2, bgcolor: 'background.paper' }}
              >
                <Toolbar sx={{ px: 0, color: 'white', ml: !isMobile ? 30 : 0 }}>
                  <IconButton onClick={handleBackToList} sx={{color: 'white'}}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ ml: 0 }}>
                    {sections.find(s => s.id === activeSection)?.label}
                  </Typography>
                </Toolbar>
              </AppBar>

              {activeSection === 'employees' ? (
                <Box flexGrow={1}>
                  <Divider sx={{ mb: 10 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-evenly', 
                    alignItems: isMobile ? 'flex-start' : 'center',
                    mb: 0,
                    gap: 0
                  }}>
                    <FloatingActionButton 
                      onClick={() => setAddModalOpen(true)}
                      tooltip="Ходим қўшиш"
                    />
                  </Box>

                  {loading ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Grid container spacing={isMobile ? 1 : 2} sx={{display: 'flex', justifyContent: 'space-evenly'}}>
                      {employees.map(employee => (
                        <Grid key={employee.id}>
                          <EmployeeCard 
                            employee={employee} 
                            refreshList={loadEmployees}
                            isMobile={isMobile}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  <AddEmployeeModal
                    open={addModalOpen}
                    refreshList={loadEmployees}
                    onClose={(shouldRefresh) => {
                      setAddModalOpen(false);
                      if (shouldRefresh) loadEmployees();
                    }}
                  />
                </Box>
              ) : activeSection === 'products' ? (loading ? (
                <LoadingAnimation loading={loading} />
              ) : (              
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    mb: 0,
                    gap: 0
                  }}>
                    <FloatingActionButton 
                      onClick={() => { setEditingProduct(null); setProductModalOpen(true); }}
                      tooltip="Махсулот қўшиш"
                    />
                  </Box>

                  <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                    <FormControl sx={{ minWidth: 120 }}>
                      <InputLabel>Фильтр по</InputLabel>
                      <Select
                        value={productFilterField}
                        onChange={(e) => setProductFilterField(e.target.value)}
                        label="Фильтр по"
                      >
                        <MenuItem value="all">Хаммаси</MenuItem>
                        <MenuItem value="name">Номи</MenuItem>
                        <MenuItem value="price">Нархи</MenuItem>
                        <MenuItem value="stock">Қолдиқ</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      placeholder="Махсулот қидириш..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Box>

                  <Grid container spacing={2} sx={{display: 'flex', justifyContent: 'space-evenly'}}>
                    {filteredProducts.map(product => (
                      <Grid key={product.id}>
                        <ProductsList
                          product={product}
                          onEdit={(product) => { setEditingProduct(product); setProductModalOpen(true); }}
                          onDelete={handleDeleteProduct}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )) : activeSection === 'clients' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    mb: 0
                  }}>
                    <FloatingActionButton 
                      onClick={() => setClientModalOpen(true)}
                      tooltip="Мижоз қўшиш"
                    />
                  </Box>

                  <AddClientModal 
                    open={clientModalOpen}
                    onClose={handleModalClose}
                    employeeId={employeeId}
                  />

                  <ClientList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                </Box>
              ) : activeSection === 'checks' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <ChecksList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                </Box>
              ) : activeSection === 'mailPhoto' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <MailPhotoList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                </Box>
              ) : activeSection === 'reviews' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <ReviewsList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                </Box>
              ) : activeSection === 'mail' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <MailList isMobile={isMobile} />
                </Box>
              ) : activeSection === 'templates' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <TemplatesList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                </Box>
              ) : activeSection === 'analytics' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <Analytics isMobile={isMobile} refreshTrigger={refreshTrigger} />
                </Box>
              ) : activeSection === 'addresses' ? (
                <Box>
                  <Divider sx={{ mb: 10 }} />
                  <AddressesList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                </Box>
              )  : (
                <CodesManager />
              )}
            </Box>
          ) : (
            <List sx={{ 
              width: '100%',
              bgcolor: 'background.paper',
              mt: 0,
            }}>
              <AppBar  
                color="transparent" 
                elevation={0}
                sx={{ mb: 3, bgcolor: 'background.paper' }}
              >
                <Toolbar sx={{ px: 0, color: 'white' }}>
                  <Typography variant="h5" sx={{ ml: isMobile ? 7 : 32 }}>
                    Бош сахифа
                  </Typography>
                </Toolbar>
              </AppBar>

              <Box sx={{mt:10}}></Box>

              {sections.map((section) => (
                <ListItem 
                  key={section.id} 
                  disablePadding
                  sx={{
                    borderBottom: '1px solid #e9ecef',
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <ListItemButton
                    onClick={() => setActiveSection(section.id)}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#c5c6c9',
                      },
                      py: 2,
                      px: 3
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'black' }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={section.label} 
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Container>

        <AddProductModal
          open={productModalOpen}
          product={editingProduct}
          onClose={(shouldRefresh) => {
            setProductModalOpen(false);
            if (shouldRefresh) loadProducts();
          }}
        />
      </MainContent>
    </Box>
  );
}